<template>
  <div class="message-popup-container">
    <!-- Show only the first message in the array -->
    <v-dialog
      v-if="currentMessage"
      v-model="currentMessage.show"
      max-width="600"
      persistent
      :retain-focus="false"
    >
      <v-card
        :style="{
          backgroundColor: currentMessage.backgroundColor || '#1976D2',
          color: currentMessage.textColor || '#FFFFFF'
        }"
      >
        <v-card-title>
          <v-icon 
            left 
            :color="currentMessage.textColor || '#FFFFFF'"
            size="28"
          >
            {{ getImportanceIcon(currentMessage.importance) }}
          </v-icon>
          {{ currentMessage.title }}
          <v-spacer></v-spacer>
          <v-chip
            :color="getImportanceColor(currentMessage.importance)"
            text-color="white"
            small
          >
            {{ getImportanceLabel(currentMessage.importance) }}
          </v-chip>
        </v-card-title>

        <v-card-text 
          class="pa-4 white"
          :style="{ color: '#000000' }"
        >
          <div 
            class="message-content"
            v-html="currentMessage.content"
          ></div>
          
          <div class="mt-4 grey--text text--darken-1">
            <v-icon small>mdi-clock-outline</v-icon>
            {{ formatDate(currentMessage.createdAt) }}
          </div>
        </v-card-text>

        <v-card-actions class="white">
          <v-checkbox
            v-model="dontShowAgain[currentMessage._id]"
            label="Diese Nachricht nicht mehr anzeigen"
            hide-details
            class="mt-0"
          ></v-checkbox>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="closeMessage(currentMessage)"
          >
            Verstanden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Multiple messages indicator -->
    <v-snackbar
      v-model="showMultipleMessagesHint"
      :timeout="5000"
      top
      right
      color="info"
    >
      Sie haben {{ totalPopupMessages }} wichtige Nachrichten
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showMultipleMessagesHint = false"
        >
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MessageService from '@/services/MessageService';
import AuthService from '@/services/AuthService';

export default {
  name: 'MessagePopup',
  
  data() {
    return {
      popupMessages: [],
      queuedMessages: [],
      dontShowAgain: {},
      showMultipleMessagesHint: false,
      dismissedMessages: new Set(this.getDismissedMessages()),
      currentUserId: null
    };
  },

  computed: {
    ...mapGetters('messages', {
      storePopupMessages: 'popupMessages'
    }),
    
    currentMessage() {
      return this.popupMessages.length > 0 ? this.popupMessages[0] : null;
    },
    
    totalPopupMessages() {
      return this.popupMessages.length + this.queuedMessages.length;
    }
  },

  watch: {
    storePopupMessages: {
      handler(newMessages, oldMessages) {
        console.log('[MessagePopup] Watcher triggered');
        console.log('[MessagePopup] Old messages count:', oldMessages?.length || 0);
        console.log('[MessagePopup] New messages count:', newMessages?.length || 0);
        
        // Always process messages when the array changes
        this.$nextTick(() => {
          this.processPopupMessages(newMessages);
        });
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    ...mapActions('messages', [
      'fetchPopupMessages',
      'markAsRead'
    ]),

    processPopupMessages(messages) {
      console.log('[MessagePopup] processPopupMessages called with:', messages);
      
      // Check if messages is valid array
      if (!messages || !Array.isArray(messages)) {
        console.log('[MessagePopup] Invalid messages, setting popupMessages to empty');
        this.popupMessages = [];
        return;
      }

      // Get all message IDs from store
      const storeMessageIds = messages.map(m => m._id);
      console.log('[MessagePopup] Store message IDs:', storeMessageIds);
      
      // Remove local messages that are no longer in store
      this.popupMessages = this.popupMessages.filter(m => 
        storeMessageIds.includes(m._id)
      );

      // Filter out dismissed messages and messages that are already shown
      const currentIds = this.popupMessages.map(m => m._id);
      console.log('[MessagePopup] Current popup message IDs:', currentIds);
      console.log('[MessagePopup] Dismissed messages:', Array.from(this.dismissedMessages));
      
      const newMessages = messages.filter(
        message => !this.dismissedMessages.has(message._id) && !currentIds.includes(message._id)
      );
      console.log('[MessagePopup] New messages to show:', newMessages);

      // Add new messages but show only one at a time
      if (newMessages.length > 0) {
        // Only show the first message immediately
        const firstMessage = {
          ...newMessages[0],
          show: true
        };
        
        // Queue the rest
        const queuedMessages = newMessages.slice(1).map(message => ({
          ...message,
          show: false
        }));
        
        // Add only the first message to popup
        this.popupMessages = [firstMessage];
        
        // Store queued messages for later display
        this.queuedMessages = queuedMessages;
        
        console.log('[MessagePopup] Showing first message, queued:', queuedMessages.length);
        
        // Show hint if multiple messages
        if (newMessages.length > 1) {
          this.showMultipleMessagesHint = true;
        }
      }
    },

    closeMessage(message) {
      // Mark as read - this will also remove it from store's popupMessages
      this.markAsRead(message._id);

      // Check if user doesn't want to see this again
      if (this.dontShowAgain[message._id]) {
        this.dismissedMessages.add(message._id);
        this.saveDismissedMessages();
      }

      // Hide current message
      if (this.currentMessage) {
        this.currentMessage.show = false;
      }
      
      // Wait for dialog animation to complete
      setTimeout(() => {
        // Remove current message
        this.popupMessages = [];
        
        // Show next message from queue after a brief delay
        if (this.queuedMessages && this.queuedMessages.length > 0) {
          this.$nextTick(() => {
            const nextMessage = this.queuedMessages.shift();
            nextMessage.show = true;
            this.popupMessages = [nextMessage];
          });
        }
      }, 350); // Slightly longer than animation time
    },

    getDismissedMessages() {
      // Get user-specific dismissed messages
      const userId = this.getCurrentUserId();
      if (!userId) return [];
      
      const dismissed = localStorage.getItem(`dismissedMessages_${userId}`);
      return dismissed ? JSON.parse(dismissed) : [];
    },

    saveDismissedMessages() {
      // Save user-specific dismissed messages
      const userId = this.getCurrentUserId();
      if (!userId) return;
      
      localStorage.setItem(
        `dismissedMessages_${userId}`, 
        JSON.stringify(Array.from(this.dismissedMessages))
      );
    },
    
    getCurrentUserId() {
      // Get current user ID from localStorage or session
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.id || user._id || null;
      } catch {
        return null;
      }
    },

    getImportanceColor(importance) {
      return MessageService.getImportanceColor(importance);
    },

    getImportanceLabel(importance) {
      return MessageService.getImportanceLabel(importance);
    },

    getImportanceIcon(importance) {
      const icons = {
        low: 'mdi-information',
        medium: 'mdi-alert-circle',
        high: 'mdi-alert',
        critical: 'mdi-alert-octagon'
      };
      return icons[importance] || 'mdi-message-alert';
    },

    formatDate(date) {
      return MessageService.formatDate(date);
    }
  },

  async mounted() {
    console.log('[MessagePopup] Component mounted');
    console.log('[MessagePopup] Initial storePopupMessages:', this.storePopupMessages);
    
    // Get current user ID and reload dismissed messages
    this.currentUserId = this.getCurrentUserId();
    this.dismissedMessages = new Set(this.getDismissedMessages());
    
    // Store current user in localStorage for future reference
    try {
      const response = await AuthService.getUser();
      if (response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
        this.currentUserId = response.data.id;
        // Reload dismissed messages for this user
        this.dismissedMessages = new Set(this.getDismissedMessages());
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Fetch popup messages on mount - will be triggered from App.vue
    // Remove duplicate fetch to avoid conflicts
  },

  beforeDestroy() {
    if (this.popupInterval) {
      clearInterval(this.popupInterval);
    }
  }
};
</script>

<style scoped>
.message-popup-container {
  position: relative;
  z-index: 9999;
}

.message-content {
  font-size: 16px;
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;
}

.message-content >>> p {
  margin-bottom: 1em;
}

.message-content >>> ul,
.message-content >>> ol {
  margin-left: 20px;
  margin-bottom: 1em;
}

.message-content >>> strong,
.message-content >>> b {
  font-weight: 600;
}

.v-dialog__content--active {
  z-index: 9999 !important;
}
</style>